import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Phone,
  ChevronDown,
  Snowflake,
  Cog,
  Wrench,
  Sparkles,
  Truck,
  Flame,
  ArrowRight,
  Menu,
  X,
} from "lucide-react";
import OpenStatus from "@/components/OpenStatus";

const SERVICE_LINKS = [
  {
    to: "/klima",
    label: "Klíma szerviz",
    desc: "R134a · R1234yf · 14 900 Ft-tól",
    Icon: Snowflake,
    accent: "#0082D2",
    testId: "nav-dropdown-klima",
  },
  {
    to: "/automatavalto-olajcsere",
    label: "MotulEVO váltóolaj-csere",
    desc: "ZF · Aisin · DSG · CVT",
    Icon: Cog,
    accent: "#E1132C",
    testId: "nav-dropdown-valto",
  },
  {
    to: "/diohejas-tisztitas",
    label: "Dióhéjas tisztítás",
    desc: "BMW N54/N55/B58 koksz-mentesítés",
    Icon: Sparkles,
    accent: "#F59E0B",
    testId: "nav-dropdown-diohej",
  },
  {
    to: "/kisteher-szerviz",
    label: "Kisteher / Ducato szervíz",
    desc: "Flotta-karbantartás · Multijet",
    Icon: Truck,
    accent: "#FF6B1A",
    testId: "nav-dropdown-kisteher",
  },
  {
    to: "/bmw",
    label: "BMW & MINI Szervíz",
    desc: "Online szervíz-vezetés · ISTA · kódolás",
    Icon: Wrench,
    accent: "#0082D2",
    testId: "nav-dropdown-bmw",
  },
  {
    to: "/ford-ecoboost-felujitas",
    label: "Ford EcoBoost felújítás",
    desc: "Blokkperselyezés · 1.0 / 1.5 / 1.6 / 2.0 / 2.3 · garancia",
    Icon: Flame,
    accent: "#0062A8",
    testId: "nav-dropdown-ford",
  },
];

function ServicesDropdown({ goToSection, onNavigate }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click / Escape only — hover opens, click/select/escape closes
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      data-testid="nav-services-dropdown"
    >
      <button
        type="button"
        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-services"
      >
        Szolgáltatások
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2.2}
        />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[min(92vw,32rem)]"
          role="menu"
          data-testid="nav-services-dropdown-panel"
        >
          <div
            className="relative backdrop-blur-xl border border-white/10 shadow-2xl"
            style={{ backgroundColor: "rgba(10,10,10,0.97)" }}
          >
            <div className="m-stripe absolute top-0 left-0 right-0 h-[2px]" />
            <div className="p-2">
              {SERVICE_LINKS.map((s) => {
                const Icon = s.Icon;
                return (
                  <Link
                    key={s.to}
                    to={s.to}
                    onClick={() => {
                      setOpen(false);
                      onNavigate?.();
                    }}
                    className="group flex items-center gap-4 p-3.5 hover:bg-white/[0.04] transition-colors"
                    role="menuitem"
                    data-testid={s.testId}
                  >
                    <div
                      className="w-11 h-11 flex items-center justify-center border shrink-0 transition-colors"
                      style={{
                        backgroundColor: `${s.accent}10`,
                        borderColor: `${s.accent}40`,
                      }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: s.accent }}
                        strokeWidth={1.6}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-bold uppercase tracking-wide text-white leading-tight"
                        style={{ fontFamily: "Barlow Condensed" }}
                      >
                        {s.label}
                      </div>
                      <div className="text-[0.7rem] text-zinc-500 mono mt-0.5 truncate">
                        {s.desc}
                      </div>
                    </div>
                    <ArrowRight
                      className="w-4 h-4 text-zinc-600 group-hover:text-white group-hover:translate-x-0.5 transition-all shrink-0"
                      strokeWidth={2}
                    />
                  </Link>
                );
              })}
            </div>

            <a
              href="/#szolgaltatasok"
              onClick={(e) => {
                setOpen(false);
                goToSection("szolgaltatasok")(e);
              }}
              className="block px-5 py-3 border-t border-white/10 text-[0.65rem] font-bold uppercase tracking-[0.24em] text-[#0082D2] hover:bg-[#0082D2]/10 transition-colors text-center"
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid="nav-dropdown-all"
            >
              Összes szolgáltatás →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const goToSection = (id) => (e) => {
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Lock body scroll when mobile menu open + close on Escape + close on route change
  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = "hidden";
    const esc = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", esc);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", esc);
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-md border-b border-white/5"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-24 flex items-center justify-between gap-2">
        <Link
          to="/"
          className="flex items-center gap-3 sm:gap-4 group"
          data-testid="nav-logo-link"
        >
          <div className="m-stripe w-1.5 h-12 sm:h-14 lg:h-16" />
          <div className="leading-none">
            <span
              className="block heading-font font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase text-white"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Autó-<span className="text-[#0082D2]">Szerelem</span>
            </span>
            <span
              className="hidden sm:block text-[0.78rem] sm:text-[0.85rem] font-bold uppercase tracking-[0.22em] text-zinc-500 mt-1.5"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Séra Autószervíz
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <ServicesDropdown goToSection={goToSection} />
          <a
            href="/#galeria"
            onClick={goToSection("galeria")}
            className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors"
            data-testid="nav-gallery"
          >
            Galéria
          </a>
          <a
            href="/#rolunk"
            onClick={goToSection("rolunk")}
            className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors"
            data-testid="nav-about"
          >
            Rólunk
          </a>
          <a
            href="/#kapcsolat"
            onClick={goToSection("kapcsolat")}
            className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-300 hover:text-white transition-colors"
            data-testid="nav-contact"
          >
            Kapcsolat
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:+36308637476"
            className="inline-flex items-center gap-2 sm:gap-3 px-2.5 sm:px-4 py-2 border border-[#0082D2]/40 hover:border-[#0082D2] hover:bg-[#0082D2]/10 text-white transition-colors group"
            data-testid="nav-phone-link"
            aria-label="Telefonhívás: +36 30 863 7476"
          >
            <Phone
              className="w-4 h-4 text-[#0082D2] group-hover:animate-pulse flex-shrink-0"
              strokeWidth={2}
            />
            <span className="hidden sm:flex flex-col items-start gap-0.5 leading-none">
              <span
                className="font-bold text-sm tracking-wide whitespace-nowrap"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                +36 30 863 7476
              </span>
              <OpenStatus />
            </span>
            {/* Mobile: tiny status dot only */}
            <span className="sm:hidden">
              <OpenStatus compact />
            </span>
          </a>

          <a
            href="/#foglalas"
            onClick={goToSection("foglalas")}
            className="nav-cta-desktop btn-primary !px-5 !py-2.5 !text-xs"
            data-testid="nav-cta-booking"
          >
            Időpontfoglalás
          </a>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-white/15 hover:border-white/40 text-white transition-colors"
            aria-label="Menü megnyitása"
            data-testid="nav-mobile-toggle"
          >
            <Menu className="w-5 h-5" strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {/* MOBILE OVERLAY MENU */}
      {mobileOpen && (
        <div
          className="md:hidden flex flex-col"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            backgroundColor: "rgba(10,10,10,0.99)",
            height: "100dvh",
            maxHeight: "100dvh",
          }}
          data-testid="nav-mobile-menu"
        >
          {/* Header bar mirroring main navbar */}
          <div className="h-16 sm:h-24 shrink-0 flex items-center justify-between px-6 border-b border-white/10">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3"
              data-testid="nav-mobile-logo"
            >
              <div className="m-stripe w-1 h-10" />
              <span
                className="font-black text-2xl tracking-tight uppercase text-white"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Autó-<span className="text-[#0082D2]">Szerelem</span>
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center justify-center w-10 h-10 border border-white/15 hover:border-white/40 text-white transition-colors"
              aria-label="Menü bezárása"
              data-testid="nav-mobile-close"
            >
              <X className="w-5 h-5" strokeWidth={1.8} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-8">
            {/* Service subpages — featured */}
            <div className="mb-8">
              <span
                className="text-[0.65rem] font-bold uppercase tracking-[0.28em] text-zinc-500 mb-4 block"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Szolgáltatások
              </span>
              <div className="space-y-2">
                {SERVICE_LINKS.map((s) => {
                  const Icon = s.Icon;
                  return (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setMobileOpen(false)}
                      className="group flex items-center gap-4 p-4 border border-white/10 hover:border-white/30 transition-colors bg-white/[0.02]"
                      data-testid={`nav-mobile-${s.testId.replace("nav-dropdown-", "")}`}
                    >
                      <div
                        className="w-12 h-12 flex items-center justify-center border shrink-0"
                        style={{
                          backgroundColor: `${s.accent}10`,
                          borderColor: `${s.accent}40`,
                        }}
                      >
                        <Icon
                          className="w-5 h-5"
                          style={{ color: s.accent }}
                          strokeWidth={1.6}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-base font-bold uppercase tracking-wide text-white leading-tight"
                          style={{ fontFamily: "Barlow Condensed" }}
                        >
                          {s.label}
                        </div>
                        <div className="text-xs text-zinc-500 mono mt-0.5 truncate">
                          {s.desc}
                        </div>
                      </div>
                      <ArrowRight
                        className="w-4 h-4 text-zinc-600 group-hover:text-white shrink-0"
                        strokeWidth={2}
                      />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Section anchors */}
            <div className="space-y-1 border-t border-white/10 pt-6">
              {[
                { id: "szolgaltatasok", label: "Összes szolgáltatás" },
                { id: "galeria", label: "Galéria" },
                { id: "rolunk", label: "Rólunk" },
                { id: "kapcsolat", label: "Kapcsolat" },
              ].map((item) => (
                <a
                  key={item.id}
                  href={`/#${item.id}`}
                  onClick={goToSection(item.id)}
                  className="flex items-center justify-between py-3.5 px-2 text-base font-bold uppercase tracking-[0.16em] text-zinc-300 hover:text-white border-b border-white/5 transition-colors"
                  style={{ fontFamily: "Barlow Condensed" }}
                  data-testid={`nav-mobile-link-${item.id}`}
                >
                  {item.label}
                  <ArrowRight className="w-4 h-4 text-zinc-600" strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

          {/* Footer CTAs */}
          <div className="shrink-0 px-6 pb-8 pt-4 border-t border-white/10 grid grid-cols-2 gap-3">
            <a
              href="tel:+36308637476"
              className="inline-flex items-center justify-center gap-2 py-3.5 border border-[#0082D2]/40 hover:border-[#0082D2] text-white transition-colors"
              data-testid="nav-mobile-phone"
            >
              <Phone className="w-4 h-4 text-[#0082D2]" strokeWidth={2} />
              <span
                className="text-xs font-bold uppercase tracking-wide"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Hívás
              </span>
            </a>
            <a
              href="/#foglalas"
              onClick={goToSection("foglalas")}
              className="btn-primary !px-5 !py-3.5 !text-xs flex items-center justify-center"
              data-testid="nav-mobile-booking"
            >
              Időpontfoglalás
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
